import { Pencil } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

interface TextIconDropdownProps {
  icon?: React.ReactNode;
  label?: string;
  value: string;
  inputOnly?: boolean;
  inputClassName?: string;
  inputContainerClassName?: string;
  onChange?: (e: string) => void;
  disabled?: boolean;
  debounceMs?: number;
}

export const TextIconInput: React.FC<TextIconDropdownProps> = ({
  icon,
  label,
  value,
  inputClassName,
  inputContainerClassName = 'p-2',
  inputOnly = false,
  onChange,
  disabled = false,
  debounceMs = 2000,
}) => {
  const [isOpen, setIsOpen] = useState(inputOnly);
  const [internalValue, setInternalValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update internal value when prop value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Handle debounced onChange
  const handleInputChange = (newValue: string) => {
    setInternalValue(newValue);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced onChange
    timeoutRef.current = setTimeout(() => {
      onChange?.(newValue);
    }, debounceMs);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const valueText = "text-base leading-6 font-normal text-black";

  return (
    <div className="relative">
      <div
        className={`${disabled ? '' : 'cursor-pointer'}`}
        onClick={(e) => {setIsOpen(true); e.stopPropagation();}}
      >
        {label && (
          <span className="text-xs font-light text-black pl-2">
            {label}
          </span>
        )}

        {/* Make this a hover group so the pencil can fade in */}
        <div className={`group flex items-center gap-2 ${inputContainerClassName}`}>
          {icon}

          {/* Let text grow/shrink without hard 50% width */}
          <div className="min-w-0 flex-1">
            {(isOpen || inputOnly) && !disabled ? (
              <input
                autoFocus
                // key bits: w-auto + (important) !min-w-0, and allow shrink in flex
                className={`text-base leading-6 font-normal text-black bg-transparent
                         border-b border-gray-800 focus:outline-none
                         w-auto !min-w-0 shrink ${inputClassName}`}
                // optional: approximate width to content
                size={Math.max(10, internalValue?.length ?? 10)}
                value={internalValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={() => setIsOpen(false)}
              />
            ) : (
              // Use a button so it's focusable; reserve the same height by keeping a transparent border
              <button
                type="button"
                className={`${valueText} w-full text-right border-b border-transparent focus:outline-none ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
                // onClick={() => setIsOpen(true)}
                title="Click to edit"
              >
                <span className="truncate">
                  {internalValue}
                </span>
              </button>
            )}
          </div>

          {/* Pencil fades in on hover of the whole row */}
          {!isOpen && !disabled && (
            <Pencil className="h-4 w-4 text-black opacity-0 transition-opacity group-hover:opacity-100" />
          )}
        </div>
      </div>
    </div>
  );
};

export default TextIconInput;
